import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPaymentUrl, parsePrice } from '../utils/payment';

/* ═══ FunnelCard ═══ карточка одного из 4 направлений на главной */
export function FunnelCard({ to, index, title, desc, cta, onMouseEnter, onMouseLeave }) {
  return (
    <Link
      to={to}
      className="FN-card"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="FN-num">{String(index + 1).padStart(2, '0')}</div>
      <div className="FN-t">{title}</div>
      <div className="FN-d">{desc}</div>
      <div className="FN-cta">{cta} →</div>
    </Link>
  );
}

/* ═══ FAQ (accordion) ═══ */
export function FaqItem({ q, a, open, onToggle }) {
  return (
    <div className={`FAQ-item ${open ? 'open' : ''}`}>
      <button type="button" className="FAQ-q" onClick={onToggle} aria-expanded={open}>
        <span>{q}</span>
        <span className="FAQ-chev" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      <div className="FAQ-a" style={{ maxHeight: open ? 400 : 0 }}>
        <div className="FAQ-a-inner">{a}</div>
      </div>
    </div>
  );
}

/* ═══ Split Text Title ═══ */
export function SplitTitle({ text, accent, visible }) {
  const chars = text.split('');
  const acChars = accent.split('');
  const n = chars.length;
  return (
    <>
      {chars.map((c, i) => <span key={i} className="spl-w"><span className={`spl-c ${visible ? 'in' : ''}`} style={{ transitionDelay: `${i * 35}ms` }}>{c === ' ' ? '\u00A0' : c}</span></span>)}
      {' '}
      <span className="ac">
        {acChars.map((c, i) => <span key={i} className="spl-w"><span className={`spl-c ${visible ? 'in' : ''}`} style={{ transitionDelay: `${(n + 1 + i) * 35}ms` }}>{c === ' ' ? '\u00A0' : c}</span></span>)}
        {visible && <span className="ac-shimmer" />}
      </span>
    </>
  );
}

/* ═══ Magnetic Button (subtle) ═══ */
export function MagBtn({ children, className, onClick, style = {}, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const handleMove = useCallback((e) => { const r = ref.current?.getBoundingClientRect(); if (!r) return; setOffset({ x: (e.clientX - r.left - r.width / 2) * 0.1, y: (e.clientY - r.top - r.height / 2) * 0.1 }); }, []);
  return <button ref={ref} className={className} onClick={onClick} onMouseMove={handleMove} onMouseEnter={onMouseEnter} onMouseLeave={(e) => { setOffset({ x: 0, y: 0 }); onMouseLeave?.(e); }} style={{ ...style, transform: `translate(${offset.x}px, ${offset.y}px)`, transition: "transform .4s cubic-bezier(.23,1,.32,1)" }}>{children}</button>;
}

/* ═══ Tilt Card (gentle) ═══ */
export function TiltCard({ children, className, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null); const [ts, setTs] = useState({});
  const handleMove = useCallback((e) => { const r = ref.current?.getBoundingClientRect(); if (!r) return; const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height; setTs({ transform: `perspective(800px) rotateX(${(y - 0.5) * -3}deg) rotateY(${(x - 0.5) * 3}deg) translateY(-4px)`, boxShadow: `${-(x - 0.5) * 10}px ${(y - 0.5) * 10}px 40px rgba(196,148,74,.06)` }); }, []);
  return <div ref={ref} className={className} onMouseMove={handleMove} onMouseEnter={onMouseEnter} onMouseLeave={(e) => { setTs({}); onMouseLeave?.(e); }} style={{ ...ts, transition: "all .5s cubic-bezier(.23,1,.32,1)", transformStyle: "preserve-3d" }}>{children}</div>;
}

/* ═══ Counter (eased) ═══ */
export function Counter({ target, suffix = "", visible }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const duration = 2000; let start = null; let raf;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (ts) => { if (!start) start = ts; const p = Math.min((ts - start) / duration, 1); setV(Math.round(ease(p) * target)); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);
  return <span>{v}{suffix}</span>;
}

/* ═══ Lightbox (with swipe + fade transition) ═══ */
export function Lightbox({ paintings, index, li, lang, sourceRect, onClose, onPrev, onNext }) {
  const touchRef = useRef(null);
  const imgRef = useRef(null);
  const [fade, setFade] = useState(false);
  const [entered, setEntered] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState("50% 50%");
  const lastTap = useRef(0);
  const prevIdx = useRef(index);

  // shared element transition from source card
  useEffect(() => {
    if (!sourceRect || !imgRef.current) { setEntered(true); return; }
    const img = imgRef.current;
    const target = img.getBoundingClientRect();
    const sx = sourceRect.width / target.width;
    const sy = sourceRect.height / target.height;
    const dx = sourceRect.left + sourceRect.width / 2 - (target.left + target.width / 2);
    const dy = sourceRect.top + sourceRect.height / 2 - (target.top + target.height / 2);
    img.style.transition = "none";
    img.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    img.style.opacity = "1";
    requestAnimationFrame(() => { requestAnimationFrame(() => {
      img.style.transition = "transform .65s cubic-bezier(.16,1,.3,1), opacity .4s";
      img.style.transform = "translate(0,0) scale(1)";
      setEntered(true);
    }); });
  }, [sourceRect]);

  useEffect(() => {
    if (prevIdx.current !== index) { setFade(true); setZoom(1); const t = setTimeout(() => setFade(false), 50); prevIdx.current = index; return () => clearTimeout(t); }
  }, [index]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") onPrev(); if (e.key === "ArrowRight") onNext(); };
    document.addEventListener("keydown", h); document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);

  const onTS = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTE = (e) => { if (touchRef.current === null) return; const d = e.changedTouches[0].clientX - touchRef.current; if (Math.abs(d) > 50) { d > 0 ? onPrev() : onNext(); } touchRef.current = null; };

  const painting = paintings[index];
  if (!painting) return null;
  return (
    <div className="LB" onClick={onClose}>
      <div className="LB-inner" onClick={(e) => e.stopPropagation()} onTouchStart={onTS} onTouchEnd={onTE}>
        <button className="LB-close" onClick={onClose}>✕</button>
        <button className="LB-arrow LB-prev" onClick={onPrev} aria-label="Previous">‹</button>
        <button className="LB-arrow LB-next" onClick={onNext} aria-label="Next">›</button>
        <div ref={imgRef} className="LB-img" style={{ background: painting.img ? "none" : painting.gradient, opacity: fade ? 0 : (entered ? 1 : 0), transition: entered ? "opacity .3s, transform .3s" : "none", transform: `scale(${zoom})`, transformOrigin: zoomOrigin, cursor: zoom > 1 ? "zoom-out" : "zoom-in" }}
          onClick={(e) => { e.stopPropagation(); if (zoom > 1) { setZoom(1); } else { const r = e.currentTarget.getBoundingClientRect(); setZoomOrigin(`${((e.clientX - r.left) / r.width * 100)}% ${((e.clientY - r.top) / r.height * 100)}%`); setZoom(2); } }}
          onTouchEnd={(e) => { const now = Date.now(); if (now - lastTap.current < 300) { setZoom(z => z > 1 ? 1 : 2); } lastTap.current = now; }}
          onWheel={(e) => { e.stopPropagation(); setZoom(z => Math.max(1, Math.min(3, z + (e.deltaY > 0 ? -0.3 : 0.3)))); }}
        >{painting.img && <img src={painting.img} alt={painting.title[0]} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}</div>
        <div className="LB-info" style={{ opacity: fade ? 0 : 1, transition: "opacity .3s" }}>
          <div className="LB-title">{painting.title[li]}</div>
          <div className="LB-medium">{painting.medium[li]}</div>
          <div className="LB-meta">{painting.size} {li === 0 ? "см" : "cm"} · {painting.year}</div>
          {painting.price && <div className="LB-price" style={{ color: painting.price === "Продано" || painting.price === "Продана" ? "var(--td)" : "var(--g)" }}>{painting.price}</div>}
          {painting.price && parsePrice(painting.price) && (() => { const url = getPaymentUrl({ amount: parsePrice(painting.price), description: painting.title[0] }); return url ? <a href={url} target="_blank" rel="noopener noreferrer" className="LB-buy">{li === 0 ? "Купить" : "Buy"}</a> : null; })()}
          <div className="LB-counter">{index + 1} / {paintings.length}</div>
        </div>
      </div>
    </div>
  );
}
